import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { StatusColors } from '@euler/components/Colors';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { kAppStoreSnapshotMode } from '@euler/functions/constants';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { TaskListItem } from '@euler/model/TaskListItem';
import { InspectionTaskStatus } from '@euler/model/enum';
import { parseTagsFromString } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDateTime } from '@euler/utils/formatters';
import isBefore from 'date-fns/isBefore';
import { AlertTriangle, Gauge } from 'lucide-react-native';
import { memo, useCallback, useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { DashedSeparator } from './components/DashedSeparator';
import { FirstTimeBadge } from './components/FirstTimeBadge';
import { FuelTypeChip } from './components/FuelTypeChip';
import { InfoCell } from './components/InfoCell';
import { MetricBadge } from './components/MetricBadge';
import { ModelBadge } from './components/ModelBadge';
import { ReportProgressRow } from './components/ReportProgressRow';
import { StatusBadgeV2 } from './components/StatusBadgeV2';
import { StatusSideBar } from './components/StatusSideBar';
import { SubscriptionChip } from './components/SubscriptionChip';
import { TagScrollView } from './components/TagScrollView';
import { TIMED_OUT, TOKENS, kLogoSize } from './constants';
import { useReportViewSummary } from './report-view-summary/useReportViewSummary';
import {
  computeInShopDuration,
  computeVehicleAge,
  deriveStatusBadge,
  formatMileage,
} from './utils';

type Props = {
  item: TaskListItem;
  style?: StyleProp<ViewStyle>;
  hideStatusLabel?: boolean;
  onPress?: (item: TaskListItem) => void;
};

export const TaskCardV2 = memo(
  ({ item, style, hideStatusLabel, onPress }: Props) => {
    const { rpx } = useResponsiveContext();

    const tags = Array.isArray(item.tags)
      ? item.tags
      : parseTagsFromString(item.tags ?? '');

    const statusBadge = useMemo(() => deriveStatusBadge(item), [item]);

    const onTaskPress = useCallback(() => {
      onPress?.(item);
    }, [onPress, item]);

    const hasTimedOut =
      item.type === 'task' &&
      item.estimatedFinishTime != null &&
      isBefore(dateFromValue(item.estimatedFinishTime), new Date());

    const ageInYears = computeVehicleAge(item.vehicleModelYear);
    const inShopDuration = computeInShopDuration(
      item.orderedAt,
      item.finishedAt ?? item.canceledAt,
    );

    const { summary, loading } = useReportViewSummary(item.orderNo);

    return (
      <TouchableOpacity
        activeOpacity={0.75}
        css={`
          background-color: ${TOKENS.cardBg};
          border-radius: ${rpx(TOKENS.cardRadius)}px;
          box-shadow: 0 ${rpx(TOKENS.cardShadowOffsetY)}px
            ${rpx(TOKENS.cardShadowBlur)}px ${TOKENS.cardShadow};
          elevation: ${TOKENS.cardElevation};
          overflow: hidden;
        `}
        style={style}
        onPress={onTaskPress}
      >
        {statusBadge ? <StatusSideBar palette={statusBadge.palette} /> : null}
        {item.isFirstTimeOrder ? <FirstTimeBadge /> : null}

        {/* HEADER */}
        <View
          css={`
            padding: ${rpx(12)}px ${rpx(14)}px;
            position: relative;
            min-height: ${rpx(58 + 24)}px;
            justify-content: center;
          `}
        >
          <Img
            uri={item.vehicleImageUrl}
            source={
              item.vehicleImageUrl
                ? undefined
                : require('@euler/assets/img/default-car-img.png')
            }
            css={`
              position: absolute;
              right: ${rpx(14)}px;
              top: ${rpx(12)}px;
              width: ${rpx(96)}px;
              height: ${rpx(58)}px;
              z-index: 0;
            `}
          />
          <View>
            {/* license plate row */}
            <HStack justifyContent="flex-start" alignItems="center">
              {item.vehicleBrandLogo ? (
                <Img
                  uri={item.vehicleBrandLogo}
                  css={`
                    width: ${rpx(kLogoSize)}px;
                    height: ${rpx(kLogoSize)}px;
                    flex-shrink: 0;
                  `}
                />
              ) : (
                <Avatar
                  name={item.vehicleName ?? item.licensePlateNo}
                  css={`
                    width: ${rpx(kLogoSize)}px;
                    height: ${rpx(kLogoSize)}px;
                    flex-shrink: 0;
                    border-radius: ${rpx(kLogoSize)}px;
                  `}
                  textStyle={{ fontSize: rpx(12) }}
                />
              )}
              <Typography.Label
                bold
                size={rpx(22)}
                lineHeight={rpx(28)}
                numberOfLines={1}
                letterSpacing={rpx(-0.5)}
                color={
                  item.isRollback
                    ? StatusColors.Danger
                    : item.status === InspectionTaskStatus.Cancelled
                    ? StatusColors.Warn
                    : TOKENS.textPrimary
                }
                decorationLine={
                  item.status === InspectionTaskStatus.Cancelled
                    ? 'line-through'
                    : undefined
                }
                css={`
                  margin-left: ${rpx(8)}px;
                  font-family: System;
                `}
              >
                {kAppStoreSnapshotMode ? '沪A555555' : item.licensePlateNo}
              </Typography.Label>
              {item.vehicleModel && item.vehicleModel !== '未知' ? (
                <ModelBadge label={item.vehicleModel} />
              ) : null}
            </HStack>

            {/* fuel / age / brand row */}
            <HStack
              justifyContent="flex-start"
              alignItems="center"
              css={`
                margin-top: ${rpx(6)}px;
                padding-left: ${rpx(kLogoSize + 8)}px;
              `}
            >
              <FuelTypeChip
                fuelType={
                  item.vehicleFuelType?.trim() &&
                  item.vehicleFuelType.trim() !== '未知'
                    ? item.vehicleFuelType.trim()
                    : '汽油'
                }
              />
              <Typography.Label
                size={rpx(11)}
                lineHeight={rpx(15)}
                color={TOKENS.textMuted}
                css={`
                  margin-left: ${rpx(8)}px;
                `}
              >
                车龄{' '}
                <Typography.Text
                  medium
                  size={rpx(11)}
                  color={TOKENS.textPrimary}
                >
                  {ageInYears != null ? `${ageInYears}年` : '-'}
                </Typography.Text>
              </Typography.Label>
              {item.vehicleBrandName ? (
                <Typography.Label
                  size={rpx(11)}
                  lineHeight={rpx(15)}
                  color={TOKENS.textMuted}
                  numberOfLines={1}
                  css={`
                    margin-left: ${rpx(6)}px;
                  `}
                >
                  · {item.vehicleBrandName}
                </Typography.Label>
              ) : null}
            </HStack>
          </View>
        </View>

        <DashedSeparator />

        {/* INFO GRID */}
        <View
          css={`
            padding: ${rpx(10)}px ${rpx(14)}px;
            flex-direction: row;
            flex-wrap: wrap;
          `}
        >
          <InfoCell
            iconNode={
              <Gauge size={rpx(12)} color={TOKENS.textFaint} strokeWidth={2} />
            }
            label="行驶里程"
            value={formatMileage(item.vehicleMileage)}
          />
          <InfoCell
            icon="clock"
            label={item.finishedAt ? '耗时' : '在厂'}
            value={inShopDuration}
          />
          <InfoCell
            icon="user"
            label="服务顾问"
            value={item.serviceAgentName}
          />
          <InfoCell
            icon="clipboard"
            label={item.finishedAt ? '完工' : '建单'}
            value={formatDateTime(
              item.finishedAt ?? item.orderedAt,
              'M/d HH:mm',
            )}
          />
        </View>

        <DashedSeparator />

        {/* STATUS ROW */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          css={`
            padding: ${rpx(10)}px ${rpx(14)}px ${rpx(12)}px;
          `}
        >
          <HStack
            justifyContent="flex-start"
            css={`
              flex: 1;
              overflow: hidden;
            `}
          >
            {!hideStatusLabel && statusBadge && (
              <StatusBadgeV2
                label={statusBadge.label}
                palette={statusBadge.palette}
              />
            )}
            {item.pendingIssueCount ? (
              <MetricBadge
                label="待处理"
                count={item.pendingIssueCount}
                palette="red"
              />
            ) : null}
            {item.maintenancePlanItemCount ? (
              <MetricBadge
                label="保养计划"
                count={item.maintenancePlanItemCount}
                palette="blue"
                solid
              />
            ) : null}
            <TagScrollView tags={tags} onPress={onTaskPress} />
          </HStack>
          <SubscriptionChip subscribed={item.subscribed ?? false} />
        </HStack>

        {/* REPORT PROGRESS */}
        <View
          css={`
            background-color: ${hasTimedOut ? TIMED_OUT.bg : TOKENS.progressBg};
            border-top-width: ${rpx(1)}px;
            border-top-color: ${TOKENS.divider};
            border-top-style: solid;
            padding: ${rpx(10)}px ${rpx(20)}px;
          `}
        >
          <HStack justifyContent="space-between" alignItems="center">
            <HStack alignItems="center">
              <Typography.Label
                size={rpx(10.5)}
                lineHeight={rpx(14)}
                color={hasTimedOut ? TIMED_OUT.textFaint : TOKENS.textFaint}
              >
                报告进度
              </Typography.Label>
              {hasTimedOut ? (
                <HStack
                  css={`
                    background-color: #dc2626;
                    border-radius: ${rpx(3)}px;
                    padding: 0 ${rpx(5)}px;
                    margin-left: ${rpx(6)}px;
                    align-items: center;
                  `}
                >
                  <AlertTriangle
                    size={rpx(8)}
                    color="#ffffff"
                    strokeWidth={2.5}
                  />
                  <Typography.Label
                    medium
                    size={rpx(9)}
                    lineHeight={rpx(13)}
                    color="#ffffff"
                    css={`
                      margin-left: ${rpx(3)}px;
                    `}
                  >
                    已超期
                  </Typography.Label>
                </HStack>
              ) : null}
            </HStack>
            <Typography.Label
              size={rpx(10.5)}
              lineHeight={rpx(14)}
              color={hasTimedOut ? TIMED_OUT.textFaint : TOKENS.textFaint}
            >
              车主查看情况
            </Typography.Label>
          </HStack>
          <View
            css={`
              margin-top: ${rpx(8)}px;
            `}
          >
            <ReportProgressRow
              item={item}
              summary={summary}
              loading={loading}
              timedOut={hasTimedOut}
              historyMode={item.finishedAt != null}
            />
          </View>
        </View>
      </TouchableOpacity>
    );
  },
);
